import { Globe, Facebook, Instagram } from "lucide-react";
import { motion } from "framer-motion";
import ShapesBackground from "./ShapesBackground";
import type { Band } from "../data/types";
import { fetchBands } from "../data/bands";
import { useEffect, useState } from "react";
import { fetchFeatureFlags } from "../config/featureFlags"

export default function BandsSection() {
  const [showBands, setShowBands] = useState(false);
  const [bands, setBands] = useState<Band[]>([]);

  useEffect(() => {
    fetchFeatureFlags().then(flags => setShowBands(flags.showBandsSection));
  }, []);

  useEffect(() => {
    fetchBands().then(setBands);
  }, []);

  if (!showBands) return null;
  return (
    <section
      id="bands"
      className="relative w-full max-w-full overflow-x-hidden py-12"
    >
      <ShapesBackground />

      <div className="relative z-10 container mx-auto px-10 max-w-full space-y-20">
        <h2 className="text-6xl mb-12 text-center text-orange font-bebas">
          Les Groupes
        </h2>

        {bands.map((band, index) => {
          const isEven = index % 2 === 0;

          return (
            <motion.div
              key={band.name}
              initial={{ x: isEven ? -50 : 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.0, ease: "easeOut" }}
              className={`flex flex-col md:flex-row items-center md:gap-x-8 ${isEven ? "" : "md:flex-row-reverse"} w-full max-w-full`}
            >
              {/* Photo */}
              <div className="md:w-1/2 w-full relative flex-shrink-0">
                <img
                  src={band.photo}
                  alt={band.name}
                  className="w-full h-100 object-cover shadow-lg max-w-full"
                />

                {/* Logo du groupe - Desktop */}
                <motion.div
                  className={`absolute w-28 h-28 rounded-full shadow-lg overflow-hidden z-20 ${isEven
                    ? "-top-8 -right-8 md:-top-10 md:-right-10"
                    : "-top-8 -left-8 md:-top-10 md:-left-10"
                    } md:block hidden`}
                  whileHover={{ scale: 1.15, rotate: 5, boxShadow: "0 0 25px #FAE810" }}
                >
                  <img
                    src={band.logo}
                    alt={`${band.name} logo`}
                    className="w-full h-full object-cover rounded-full"
                  />
                </motion.div>

                {/* Logo mobile */}
                <motion.div
                  className="absolute md:hidden bottom-[-2.5rem] left-1/2 -translate-x-1/2 w-20 h-20 rounded-full shadow-lg overflow-hidden z-20"
                >
                  <img
                    src={band.logo}
                    alt={`${band.name} logo`}
                    className="w-full h-full object-cover rounded-full"
                  />
                </motion.div>
              </div>

              {/* Infos du groupe */}
              <div className="md:w-1/2 w-full mt-8 md:mt-0 text-center md:text-left bg-white/80 backdrop-blur-sm p-6 flex-shrink-0">
                <h3 className="text-4xl text-bleuClair font-bebas">{band.name}</h3>
                <p className="text-m text-rouge font-medium mb-2 ">
                  {band.time}
                  {band.conductor && (
                    ` | Direction : ${band.conductor}`
                  )}
                </p>
                {Array.isArray(band.description) ? (
                  band.description.map((para, i) => (
                    <p
                      key={i}
                      className="mb-1 text-bleuFonce break-words text-justify"
                    >
                      {para}
                    </p>
                  ))
                ) : (
                  <p className="mb-4 text-bleuFonce break-words text-justify">
                    {band.description}
                  </p>
                )}

                {/* Icônes Contact et Réseaux */}
                <div className="flex space-x-4 justify-center md:justify-start">
                  {band.facebook && (
                    <a href={band.facebook} target="_blank" rel="noreferrer" className="text-orange hover:text-rouge hover:fill-rouge transition-colors duration-500">
                      <Facebook size={20} strokeWidth={1} fill="currentColor" />
                    </a>
                  )}
                  {band.instagram && (
                    <a href={band.instagram} target="_blank" rel="noreferrer" className="text-orange hover:text-rouge transition-colors duration-500">
                      <Instagram size={20} />
                    </a>
                  )}
                  {band.website && (
                    <a href={band.website} target="_blank" rel="noreferrer" className="text-orange hover:text-rouge transition-colors duration-500">
                      <Globe size={20} />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}